/**
 * Fetches undismissed unopened e-card warning count for the main-app nav badge (PBI #1160).
 *
 * Behaviour mirrors {@link useMySupportUnreadCount}:
 * - Fetches when authenticated + terms accepted
 * - Refreshes on {@link UNOPENED_WARNINGS_CHANGED_EVENT} (dismiss / send / resend)
 * - Polls while the tab is visible and re-fetches on focus (recovers from
 *   auth-token races and after Send Now without requiring a full reload)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import accountService from '../services/accountService';
import { UNOPENED_WARNINGS_CHANGED_EVENT } from '../constants/unopenedCardFollowUp';
import { useAuth } from '../contexts/AuthContext';

/** Default poll interval — same cadence as the Contact Us unread badge. */
const DEFAULT_POLL_INTERVAL_MS = 60_000;

/**
 * Returns the count of undismissed unopened e-card warnings for the current user.
 *
 * @param pollIntervalMs - Poll interval; pass 0 to disable polling
 */
export function useUnopenedEcardWarningCount(
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): number {
  const { isAuthenticated, termsAccepted } = useAuth();
  const enabled = isAuthenticated && termsAccepted;
  const [count, setCount] = useState(0);
  const hasLoadedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      const warnings = await accountService.getUnopenedEcardWarnings();
      setCount(warnings.length);
    } catch (err) {
      // Keep the last known count on transient failures (auth race, blip).
      console.error('Failed to load unopened e-card warning count:', err);
    } finally {
      hasLoadedRef.current = true;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      hasLoadedRef.current = false;
      return;
    }

    void refresh();

    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (pollIntervalMs > 0) {
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          void refresh();
        }
      }, pollIntervalMs);
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onWarningsChanged = () => {
      void refresh();
    };
    window.addEventListener(UNOPENED_WARNINGS_CHANGED_EVENT, onWarningsChanged);

    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(UNOPENED_WARNINGS_CHANGED_EVENT, onWarningsChanged);
    };
  }, [enabled, pollIntervalMs, refresh]);

  return count;
}
